import { useMemo } from 'react'

import {
  getRevenueAndSobBaseline,
  getRevenueAndSobPlan,
  type IRevenueAndSobFigures,
} from '../../lib'

import { useCockpitSettings } from './useCockpitSettings'
import { useEffectiveActualsSync } from './useEffectiveActualsSync'
import { useFilteredFinSobRows } from './useFilteredFinSobRows'

export interface ICockpitBaselineAndPlan {
  /** Last year's revenue and SOB — what this year grows from. */
  baseline: IRevenueAndSobFigures
  /** This year's target. */
  plan: IRevenueAndSobFigures
  fYear: number
}

/**
 * The Revenue & SOB tile's baseline and plan columns. Shared because the Initiatives tile reads
 * them too — its goal-coverage figures are the initiatives' goals expressed as a share of the
 * financial plan and of the plan's own growth over baseline.
 */
export const useCockpitBaselineAndPlan = (): ICockpitBaselineAndPlan => {
  const { actualsType, isLocalCurrency, isYtd, selectedCategory } = useCockpitSettings()

  const { targetRows, actualsRows } = useFilteredFinSobRows()
  const { fYear, currentFQuarter, fQuarterActualsPercentage } = useEffectiveActualsSync()

  const baseline = useMemo(
    () =>
      getRevenueAndSobBaseline({
        rows: actualsRows,
        selectedFYear: fYear,
        actualsType,
        isLocalCurrency,
        currentFQuarter,
        fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
        category: selectedCategory,
      }),
    [
      actualsRows,
      fYear,
      actualsType,
      isLocalCurrency,
      isYtd,
      currentFQuarter,
      fQuarterActualsPercentage,
      selectedCategory,
    ]
  )

  const plan = useMemo(
    () =>
      getRevenueAndSobPlan({
        rows: targetRows,
        selectedFYear: fYear,
        actualsType,
        isLocalCurrency,
        currentFQuarter,
        fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
        category: selectedCategory,
      }),
    [
      targetRows,
      fYear,
      actualsType,
      isLocalCurrency,
      isYtd,
      currentFQuarter,
      fQuarterActualsPercentage,
      selectedCategory,
    ]
  )

  return {
    baseline,
    plan,
    fYear,
  }
}
